import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { alpha, Box, Typography } from "@mui/material";
import type { FC, ReactNode } from "react";
import { ALPHA, BADGE, FONT_SIZE, FONT_WEIGHT, ICON_SIZE, ICON_TILE, INK, LINE_HEIGHT, MOTION } from "@/theme/tokens";
import { EyebrowLabel } from "../molecules/EyebrowLabel";
import { chevronSx, isRichSection } from "./collapsibleSectionSx";
import { CollapsibleSectionVariant } from "./collapsibleSectionVariant";

type TitleProps = { title: string; titleId?: string; description?: string };

/** A `Section` title with its glyph and description beside it. */
const RichSectionTitle: FC<TitleProps & { icon?: ReactNode }> = ({ title, icon, description, titleId }) => (
	<Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
		{icon !== undefined && (
			<Box component="span" aria-hidden sx={{ display: "inline-flex", flexShrink: 0 }}>
				{icon}
			</Box>
		)}
		<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
			<Typography component="span" variant="body2" id={titleId} sx={{ color: "primary.dark", fontWeight: FONT_WEIGHT.SEMIBOLD }}>
				{title}
			</Typography>
			{description !== undefined && (
				<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, lineHeight: LINE_HEIGHT.DENSE_PROSE }}>
					{description}
				</Typography>
			)}
		</Box>
	</Box>
);

/** The `Disclosure` header: a title at card-heading size and the line saying what opening it reveals. */
const DisclosureTitle: FC<TitleProps & { open: boolean }> = ({ title, titleId, description, open }) => (
	<>
		<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
			<Typography
				component="span"
				id={titleId}
				sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: FONT_WEIGHT.BOLD, color: INK.PRIMARY, lineHeight: LINE_HEIGHT.TIGHT }}
			>
				{title}
			</Typography>
			{description !== undefined && (
				<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, lineHeight: LINE_HEIGHT.SNUG }}>
					{description}
				</Typography>
			)}
		</Box>
		{/* In a brand disc that deepens when open: on a card-wide row a bare muted chevron at the far end reads as decoration. */}
		<Box
			component="span"
			aria-hidden
			sx={(theme) => ({
				width: ICON_TILE.XS.size,
				height: ICON_TILE.XS.size,
				borderRadius: BADGE.PILL_RADIUS_FULL,
				flexShrink: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: "primary.main",
				backgroundColor: alpha(theme.palette.primary.main, open ? ALPHA.CONTROL_TINT_HOVER : ALPHA.HINT_TINT),
				transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			})}
		>
			<ExpandMore sx={[chevronSx(open), { fontSize: ICON_SIZE.CONTROL, color: "inherit" }]} />
		</Box>
	</>
);

type HeaderContentProps = TitleProps & { variant: CollapsibleSectionVariant; open: boolean; icon?: ReactNode; summary?: string };

export const HeaderContent: FC<HeaderContentProps> = ({ variant, title, open, icon, summary, titleId, description }) => {
	if (variant === CollapsibleSectionVariant.Disclosure) return <DisclosureTitle title={title} titleId={titleId} description={description} open={open} />;
	if (variant === CollapsibleSectionVariant.Eyebrow) {
		return (
			<>
				<EyebrowLabel component="span" id={titleId} sx={{ flexShrink: 0 }}>
					{title}
				</EyebrowLabel>
				{summary !== undefined && (
					<Typography
						component="span"
						sx={{ minWidth: 0, color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
					>
						{summary}
					</Typography>
				)}
				<ExpandMore aria-hidden sx={[chevronSx(open), { fontSize: ICON_SIZE.MICRO, marginInlineStart: "auto" }]} />
			</>
		);
	}
	if (variant === CollapsibleSectionVariant.Inline) {
		return (
			<>
				{icon !== undefined && (
					<Box component="span" aria-hidden sx={{ display: "inline-flex", "& > svg": { fontSize: ICON_SIZE.MICRO } }}>
						{icon}
					</Box>
				)}
				<Typography component="span" id={titleId} sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, fontWeight: FONT_WEIGHT.MEDIUM }}>
					{title}
				</Typography>
				<ExpandMore aria-hidden sx={[chevronSx(open), { fontSize: ICON_SIZE.MICRO }]} />
			</>
		);
	}
	return (
		<>
			{isRichSection(variant, icon, description) ? (
				<RichSectionTitle title={title} icon={icon} description={description} titleId={titleId} />
			) : (
				<Typography component="span" variant="body2" id={titleId} sx={{ color: "primary.dark", fontWeight: FONT_WEIGHT.SEMIBOLD }}>
					{title}
				</Typography>
			)}
			<ArrowDownward aria-hidden sx={[chevronSx(open), { fontSize: { xs: 16, xl: 20 } }]} />
		</>
	);
};
